import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { announcementAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import AnnouncementFormFields from '../../components/admin/AnnouncementFormFields'
import AnnouncementScheduling from '../../components/admin/AnnouncementScheduling'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AdminAnnouncementForm')

/**
 * AnnouncementForm - Create/edit announcement page
 * Orchestrates form data management and API calls
 * Optimized from 320 lines by extracting form field components
 */
export default function AnnouncementForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading, error, execute } = useAsyncOperation()
  const isEditMode = id && id !== 'new'

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    language: 'en',
    announcementType: 'general',
    priority: 'normal',
    startDate: '',
    endDate: '',
    isPublished: false,
    translationGroupId: ''
  })

  useEffect(() => {
    if (isEditMode) {
      loadAnnouncement()
    }
  }, [id])

  const loadAnnouncement = async () => {
    await execute(async () => {
      const response = await announcementAPI.getAnnouncement(id)
      const announcement = response.data
      setFormData({
        title: announcement.title || '',
        content: announcement.content || '',
        language: announcement.language || 'en',
        announcementType: announcement.announcementType || 'general',
        priority: announcement.priority || 'normal',
        startDate: announcement.startDate ? new Date(announcement.startDate).toISOString().slice(0, 16) : '',
        endDate: announcement.endDate ? new Date(announcement.endDate).toISOString().slice(0, 16) : '',
        isPublished: announcement.isPublished || false,
        translationGroupId: announcement.translationGroupId || ''
      })
    })
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    await execute(async () => {
      const payload = {
        title: formData.title,
        content: formData.content,
        language: formData.language,
        announcementType: formData.announcementType,
        priority: formData.priority,
        publishedAt: formData.startDate || null,
        expiresAt: formData.endDate || null,
        isPublished: formData.isPublished,
        translationGroupId: formData.translationGroupId || null
      }

      try {
        if (isEditMode) {
          await announcementAPI.updateAnnouncement(id, payload)
        } else {
          await announcementAPI.createAnnouncement(payload)
        }
        navigate('/admin/announcements')
      } catch (error) {
        logger.error('Backend error response', error, {
          errorData: error.response?.data
        })
        throw error
      }
    }, { 
      successMessage: isEditMode 
        ? 'Announcement updated successfully' 
        : 'Announcement created successfully' 
    })
  }

  if (loading && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading announcement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/announcements')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('admin.backToCollection')}
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Announcement' : 'Create Announcement'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditMode ? 'Update announcement details' : 'Add a new announcement'}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <AnnouncementFormFields
              formData={formData}
              onChange={handleChange}
            />

            <AnnouncementScheduling
              formData={formData}
              onChange={handleChange}
            />

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/announcements')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
